import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { HighlightedText } from "@/components/HighlightText"
import type { JsonFieldValue } from "@/types/json"
import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

interface TemplateVariable {
  start: number
  end: number
  content: string
  fullMatch: string
}

export type TemplateEntityValues = Record<
  string,
  Record<string, JsonFieldValue>
>

const TEMPLATE_VARIABLE_DROPDOWN_MODE = {
  ENTITY: "entity",
  ATTRIBUTE: "attribute",
} as const

type TemplateVariableDropdownMode =
  (typeof TEMPLATE_VARIABLE_DROPDOWN_MODE)[keyof typeof TEMPLATE_VARIABLE_DROPDOWN_MODE]

const TEMPLATE_VARIABLE_SEGMENT_TYPE = {
  TEXT: "text",
  VARIABLE: "variable",
} as const

type TemplateVariableSegmentType =
  (typeof TEMPLATE_VARIABLE_SEGMENT_TYPE)[keyof typeof TEMPLATE_VARIABLE_SEGMENT_TYPE]

interface TemplateVariableSegment {
  type: TemplateVariableSegmentType
  content: string
  variable?: TemplateVariable
  key: string
}

interface TemplateVariableInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  chipClassName?: string
  chipColor?:
    | "default"
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "danger"
  renderChip?: (variable: TemplateVariable, index: number) => React.ReactNode
  onVariableChange?: (variables: TemplateVariable[]) => void
  disabled?: boolean
  padding?: string
  entityAttributes?: Record<string, string[]>
  entityValues?: TemplateEntityValues
  replaceVariables?: boolean
  highlightText?: string
  isHighlighted?: boolean
  focusOnMount?: boolean
}

interface DropdownState {
  show: boolean
  mode: TemplateVariableDropdownMode
  selectedEntity: string
  selectedIndex: number
  cursorPosition: number
}

/**
 * Parse template variables from text
 */
const parseTemplateVariables = (text: string): TemplateVariable[] => {
  const variables: TemplateVariable[] = []
  const regex = /\{\{([^{}]+)\}\}/g
  let match

  while ((match = regex.exec(text)) !== null) {
    variables.push({
      start: match.index,
      end: match.index + match[0].length - 1,
      content: match[1].trim(),
      fullMatch: match[0],
    })
  }

  return variables
}

/**
 * Replace template variables with actual values
 */
const replaceTemplateVariables = (
  text: string,
  entityValues?: TemplateEntityValues,
): string => {
  if (!entityValues) {
    return text
  }

  return text.replace(
    /\{\{(\w+)\.(\w+)\}\}/g,
    (match: string, entity: string, attribute: string) => {
      const entityData = entityValues[entity]
      if (entityData?.[attribute] !== undefined) {
        return stringifyUnknownValue(entityData[attribute])
      }
      return match
    },
  )
}

/**
 * Split text into segments for rendering
 */
const splitTextWithVariables = (
  text: string,
  variables: TemplateVariable[],
): TemplateVariableSegment[] => {
  if (variables.length === 0) {
    return [
      {
        type: TEMPLATE_VARIABLE_SEGMENT_TYPE.TEXT,
        content: text,
        key: "text-0",
      },
    ]
  }

  const segments: TemplateVariableSegment[] = []
  let lastIndex = 0

  variables.forEach((variable, index) => {
    if (variable.start > lastIndex) {
      segments.push({
        type: TEMPLATE_VARIABLE_SEGMENT_TYPE.TEXT,
        content: text.substring(lastIndex, variable.start),
        key: `text-${lastIndex}`,
      })
    }

    segments.push({
      type: TEMPLATE_VARIABLE_SEGMENT_TYPE.VARIABLE,
      content: variable.fullMatch,
      variable,
      key: `var-${variable.start}-${index}`,
    })

    lastIndex = variable.end + 1
  })

  if (lastIndex < text.length) {
    segments.push({
      type: TEMPLATE_VARIABLE_SEGMENT_TYPE.TEXT,
      content: text.substring(lastIndex),
      key: `text-${lastIndex}`,
    })
  }

  return segments
}

/**
 * Check if cursor is inside an incomplete variable
 */
const isInsideIncompleteVariable = (
  text: string,
  cursorPos: number,
): { isInside: boolean; startPos: number; beforeDot: boolean } => {
  const textBefore = text.substring(0, cursorPos)
  const textAfter = text.substring(cursorPos)

  // Find the last {{ before cursor
  const lastOpenIndex = textBefore.lastIndexOf("{{")
  if (lastOpenIndex === -1) {
    return { isInside: false, startPos: -1, beforeDot: false }
  }

  // Check if there's a closing }} after the last {{
  const nextCloseIndex = textAfter.indexOf("}}")
  const hasClosingBeforeCursor = textBefore
    .substring(lastOpenIndex)
    .includes("}}")

  // If there's a }} before cursor after the last {{, we're not inside an incomplete variable
  if (hasClosingBeforeCursor) {
    return { isInside: false, startPos: -1, beforeDot: false }
  }

  // If there's a closing }} after cursor, we're inside a complete variable
  if (nextCloseIndex !== -1) {
    return { isInside: false, startPos: -1, beforeDot: false }
  }

  // We're inside an incomplete variable
  const textInVariable = textBefore.substring(lastOpenIndex + 2)
  const hasDot = textInVariable.includes(".")

  return {
    isInside: true,
    startPos: lastOpenIndex,
    beforeDot: !hasDot,
  }
}

export const TemplateVariableInput: React.FC<TemplateVariableInputProps> = ({
  value,
  onChange,
  placeholder,
  className = "",
  chipClassName = "",
  renderChip,
  onVariableChange,
  disabled = false,
  padding = "p-0",
  entityAttributes = {},
  entityValues = {},
  replaceVariables = false,
  highlightText,
  isHighlighted = false,
  focusOnMount = false,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const isComposingRef = useRef(false)

  // Auto-resize textarea and container
  const adjustTextareaHeight = useCallback(() => {
    if (!textareaRef.current) {
      return
    }
    const textarea = textareaRef.current
    textarea.style.height = "auto"
    if (containerRef.current) {
      containerRef.current.style.height = "auto"
    }
    const newHeight = textarea.scrollHeight
    textarea.style.height = `${newHeight}px`
    if (containerRef.current) {
      containerRef.current.style.height = `${newHeight}px`
    }
  }, [])

  const entityOptions = useMemo(() => ["test_case", "agent"], [])
  const listboxId = React.useId()
  const [dropdown, setDropdown] = useState<DropdownState>({
    show: false,
    mode: TEMPLATE_VARIABLE_DROPDOWN_MODE.ENTITY,
    selectedEntity: "",
    selectedIndex: 0,
    cursorPosition: 0,
  })

  // Parse variables
  const variables = useMemo(() => parseTemplateVariables(value), [value])

  // Notify parent of variable changes
  useEffect(() => {
    onVariableChange?.(variables)
  }, [variables, onVariableChange])

  // Get display value (replaced or original)
  const displayValue = useMemo(() => {
    if (replaceVariables) {
      return replaceTemplateVariables(value, entityValues)
    }
    return value
  }, [value, replaceVariables, entityValues])

  // Split into segments
  const segments = useMemo(
    () => splitTextWithVariables(value, variables),
    [value, variables],
  )

  const currentOptions = useMemo(() => {
    if (dropdown.mode === TEMPLATE_VARIABLE_DROPDOWN_MODE.ENTITY) {
      return entityOptions
    } else if (
      dropdown.mode === TEMPLATE_VARIABLE_DROPDOWN_MODE.ATTRIBUTE &&
      dropdown.selectedEntity
    ) {
      return entityAttributes[dropdown.selectedEntity] || []
    }
    return []
  }, [dropdown.mode, dropdown.selectedEntity, entityAttributes, entityOptions])

  // Update dropdown state based on cursor position
  const updateDropdown = useCallback(
    (cursorPos: number) => {
      if (isComposingRef.current) {
        return
      }

      const textBefore = value.substring(0, cursorPos)
      const incomplete = isInsideIncompleteVariable(value, cursorPos)

      if (!incomplete.isInside) {
        setDropdown((prev) => ({ ...prev, show: false }))
        return
      }

      const variableText = textBefore.substring(incomplete.startPos + 2)

      // Check for {{
      if (variableText === "" || incomplete.beforeDot) {
        // Check for {{entity.
        const entityMatch = variableText.match(/^(\w+)\.$/)
        if (entityMatch) {
          const entity = entityMatch[1]
          if (entityOptions.includes(entity)) {
            setDropdown({
              show: true,
              mode: TEMPLATE_VARIABLE_DROPDOWN_MODE.ATTRIBUTE,
              selectedEntity: entity,
              selectedIndex: 0,
              cursorPosition: cursorPos,
            })
            return
          }
        }

        // Just {{
        setDropdown({
          show: true,
          mode: TEMPLATE_VARIABLE_DROPDOWN_MODE.ENTITY,
          selectedEntity: "",
          selectedIndex: 0,
          cursorPosition: cursorPos,
        })
      } else {
        setDropdown((prev) => ({ ...prev, show: false }))
      }
    },
    [value, entityOptions],
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value
      const cursorPos = e.target.selectionStart

      onChange(newValue)
      updateDropdown(cursorPos)

      // Adjust height after value change
      requestAnimationFrame(() => {
        adjustTextareaHeight()
      })
    },
    [onChange, updateDropdown, adjustTextareaHeight],
  )

  // Handle selection change (cursor movement)
  const handleSelect = useCallback(
    (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
      if (dropdown.show) {
        return
      } // Don't update during dropdown navigation

      const target = e.target as HTMLTextAreaElement
      const cursorPos = target.selectionStart
      updateDropdown(cursorPos)
    },
    [dropdown.show, updateDropdown],
  )

  // Insert variable
  const insertVariable = useCallback(
    (option: string) => {
      if (!textareaRef.current) {
        return
      }

      const textarea = textareaRef.current
      const cursorPos = dropdown.cursorPosition
      const textBefore = value.substring(0, cursorPos)
      const textAfter = value.substring(cursorPos)

      let newValue: string
      let newCursorPos: number

      if (dropdown.mode === TEMPLATE_VARIABLE_DROPDOWN_MODE.ENTITY) {
        // Replace {{ with {{entity.
        const lastOpenIndex = textBefore.lastIndexOf("{{")
        const beforeVar = textBefore.substring(0, lastOpenIndex)
        newValue = `${beforeVar}{{${option}.${textAfter}`
        newCursorPos = beforeVar.length + `{{${option}.`.length

        // Keep dropdown open for attribute selection
        setDropdown({
          show: true,
          mode: TEMPLATE_VARIABLE_DROPDOWN_MODE.ATTRIBUTE,
          selectedEntity: option,
          selectedIndex: 0,
          cursorPosition: newCursorPos,
        })
      } else {
        // Replace {{entity. with {{entity.attribute}}
        const lastOpenIndex = textBefore.lastIndexOf("{{")
        const beforeVar = textBefore.substring(0, lastOpenIndex)
        const entity = dropdown.selectedEntity
        newValue = `${beforeVar}{{${entity}.${option}}}${textAfter}`
        newCursorPos = beforeVar.length + `{{${entity}.${option}}}`.length

        setDropdown((prev) => ({ ...prev, show: false }))
      }

      onChange(newValue)

      // Update cursor position
      requestAnimationFrame(() => {
        if (textarea === textareaRef.current) {
          textarea.focus()
          textarea.setSelectionRange(newCursorPos, newCursorPos)
        }
      })
    },
    [value, dropdown, onChange],
  )

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!dropdown.show || currentOptions.length === 0) {
        return
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault()
          setDropdown((prev) => ({
            ...prev,
            selectedIndex: Math.min(
              prev.selectedIndex + 1,
              currentOptions.length - 1,
            ),
          }))
          break

        case "ArrowUp":
          e.preventDefault()
          setDropdown((prev) => ({
            ...prev,
            selectedIndex: Math.max(prev.selectedIndex - 1, 0),
          }))
          break

        case "Enter":
        case "Tab":
          e.preventDefault()
          if (currentOptions[dropdown.selectedIndex]) {
            insertVariable(currentOptions[dropdown.selectedIndex])
          }
          break

        case "Escape":
          e.preventDefault()
          setDropdown((prev) => ({ ...prev, show: false }))
          break
      }
    },
    [dropdown.show, dropdown.selectedIndex, currentOptions, insertVariable],
  )

  // Handle composition events (IME input)
  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true
  }, [])

  const handleCompositionEnd = useCallback(() => {
    isComposingRef.current = false
    if (textareaRef.current) {
      updateDropdown(textareaRef.current.selectionStart)
      adjustTextareaHeight()
    }
  }, [updateDropdown, adjustTextareaHeight])

  // Ref callback to adjust height when textarea is mounted
  const textareaRefCallback = useCallback(
    (node: HTMLTextAreaElement | null) => {
      textareaRef.current = node
      if (node) {
        // Adjust height when mounted - use double RAF to ensure DOM is ready
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (node === textareaRef.current) {
              adjustTextareaHeight()
              if (focusOnMount) {
                node.focus()
                node.setSelectionRange(node.value.length, node.value.length)
              }
            }
          })
        })
      }
    },
    [adjustTextareaHeight, focusOnMount],
  )

  // Calculate dropdown position
  const dropdownStyle = useMemo(() => {
    if (!dropdown.show || !textareaRef.current || !containerRef.current) {
      return { display: "none" }
    }

    const textarea = textareaRef.current
    const textBefore = value.substring(0, dropdown.cursorPosition)
    const lines = textBefore.split("\n")
    const currentLine = lines.length
    const lineHeight = parseInt(
      window.getComputedStyle(textarea).lineHeight,
      10,
    )

    // Simple positioning: below current line
    const top = currentLine * lineHeight + 4

    return {
      top: `${top}px`,
      left: "0px",
      display: "block",
    }
  }, [dropdown.show, dropdown.cursorPosition, value])

  // Default chip renderer
  const defaultRenderChip = useCallback(
    (variable: TemplateVariable) => {
      // Determine color based on entity type
      const entityMatch = variable.content.match(/^(\w+)\./)
      const entity = entityMatch ? entityMatch[1] : ""

      let colorClass = "text-coval-info-text" // default
      if (entity === "agent") {
        colorClass = "text-blue-600 dark:text-blue-400"
      } else if (entity === "test_case") {
        colorClass = "text-green-600 dark:text-green-400"
      }

      return (
        <span
          className={`inline ${colorClass} font-medium ${chipClassName}`}
          style={{
            verticalAlign: "baseline",
            lineHeight: "inherit",
            font: "inherit",
            letterSpacing: "inherit",
            wordSpacing: "inherit",
          }}
        >
          {variable.fullMatch}
        </span>
      )
    },
    [chipClassName],
  )

  const listOpen = dropdown.show && currentOptions.length > 0
  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
      style={{ height: "auto" }}
    >
      <textarea
        ref={textareaRefCallback}
        value={value}
        onChange={handleChange}
        onSelect={handleSelect}
        onKeyDown={handleKeyDown}
        aria-controls={listOpen ? listboxId : undefined}
        aria-activedescendant={
          listOpen && dropdown.selectedIndex < currentOptions.length
            ? `${listboxId}-${dropdown.selectedIndex}`
            : undefined
        }
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        placeholder={placeholder}
        disabled={disabled}
        className={`absolute inset-0 w-full resize-none bg-transparent text-transparent z-10 outline-none border-none overflow-hidden ${padding}`}
        style={{
          WebkitTextFillColor: "transparent",
          caretColor: "var(--coval-text)",
          color: "transparent",
          height: "auto",
          font: "inherit",
          lineHeight: "inherit",
          letterSpacing: "inherit",
          wordSpacing: "inherit",
          textIndent: "inherit",
          textTransform: "inherit",
        }}
      />

      <div
        className={`absolute inset-0 w-full pointer-events-none z-0 overflow-hidden whitespace-pre-wrap wrap-break-word ${padding}`}
        style={{
          wordBreak: "break-word",
          font: "inherit",
          lineHeight: "inherit",
          letterSpacing: "inherit",
          wordSpacing: "inherit",
          textIndent: "inherit",
          textTransform: "inherit",
        }}
      >
        {replaceVariables ? (
          <span className="inline">
            {highlightText ? (
              <HighlightedText
                text={displayValue || ""}
                searchText={highlightText}
                isCurrentMatch={isHighlighted}
              />
            ) : (
              displayValue || (
                <span className="text-coval-text-muted">{placeholder}</span>
              )
            )}
          </span>
        ) : (
          <>
            {segments.map((segment) => {
              if (
                segment.type === TEMPLATE_VARIABLE_SEGMENT_TYPE.VARIABLE &&
                segment.variable
              ) {
                return (
                  <span key={segment.key} className="inline">
                    {renderChip
                      ? renderChip(segment.variable, 0)
                      : defaultRenderChip(segment.variable)}
                  </span>
                )
              }
              return highlightText ? (
                <HighlightedText
                  key={segment.key}
                  text={segment.content}
                  searchText={highlightText}
                  isCurrentMatch={isHighlighted}
                  className="inline whitespace-pre-wrap"
                />
              ) : (
                <span key={segment.key} className="inline whitespace-pre-wrap">
                  {segment.content}
                </span>
              )
            })}
            {!value && placeholder && (
              <span className="text-coval-text-muted">{placeholder}</span>
            )}
          </>
        )}
      </div>

      {listOpen && (
        <div
          ref={dropdownRef}
          id={listboxId}
          role="listbox"
          aria-label="Template variables"
          tabIndex={-1}
          className="absolute z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg min-w-[120px] max-h-[200px] overflow-auto"
          style={dropdownStyle}
          onMouseDown={(e) => e.preventDefault()}
        >
          {currentOptions.map((option, index) => (
            <button
              type="button"
              key={option}
              id={`${listboxId}-${index}`}
              role="option"
              aria-selected={index === dropdown.selectedIndex}
              tabIndex={-1}
              onClick={() => insertVariable(option)}
              onMouseEnter={() =>
                setDropdown((prev) => ({ ...prev, selectedIndex: index }))
              }
              className={`block w-full px-3 py-2 text-left cursor-pointer transition-colors ${
                index === dropdown.selectedIndex
                  ? "bg-coval-info-surface text-coval-info-on-surface"
                  : "hover:bg-surface-1"
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
